/**
 * Chip — small pill, two visual states:
 *   active  → ink bg, cream text (selected filter)
 *   resting → chip bg, ink text (default)
 *
 * Optional `tone` overrides the active palette for the password-strength
 * pattern (coral-soft + coral-ink) and similar accent uses.
 */
import { ButtonHTMLAttributes, ReactNode } from 'react';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  size?: 'sm' | 'md';
  icon?: ReactNode;
  tone?: 'default' | 'coral' | 'ink';
  children?: ReactNode;
};

export function Chip({
  active,
  size = 'md',
  icon,
  tone = 'default',
  children,
  style,
  ...rest
}: Props) {
  const h = size === 'sm' ? 28 : 34;
  const palette = (() => {
    if (active && tone === 'coral') return { bg: 'var(--coral-soft)', fg: 'var(--coral-ink)' };
    if (active && tone === 'ink')   return { bg: 'var(--ink)', fg: 'var(--bg)' };
    if (active)                     return { bg: 'var(--ink)', fg: 'var(--bg)' };
    return { bg: 'var(--chip)', fg: 'var(--ink)' };
  })();
  return (
    <button
      {...rest}
      style={{
        height: h,
        padding: '0 14px',
        borderRadius: h,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: palette.bg,
        color: palette.fg,
        fontSize: size === 'sm' ? 12 : 13,
        fontWeight: 500,
        letterSpacing: -0.1,
        whiteSpace: 'nowrap',
        border: 'none',
        cursor: 'pointer',
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}
